import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { callingAPI } from '../api';
import { useAuth } from '../context/AuthContext';

const TERMINAL = ['ENDED', 'DEVICE_OFFLINE', 'DEVICE_NOT_REGISTERED', 'PERMISSION_DENIED', 'EXPIRED', 'CANCELLED', 'FAILED'];

// Shared GNONS Click-to-Call handler. Sends a dial COMMAND to the employee's
// registered/default Android device (NO browser tel:). Never creates a call record itself.
export function useClickToCall() {
  const { user } = useAuth();
  const [dialing, setDialing] = useState(false);
  const [status, setStatus] = useState(null);
  const [selection, setSelection] = useState(null);
  const activeRef = useRef(false);

  const poll = useCallback(async (id, tries) => {
    if (!id || tries > 20) { setDialing(false); return; }
    try {
      const { data } = await callingAPI.getCommand(id);
      setStatus(data.status);
      if (TERMINAL.includes(data.status)) {
        setDialing(false);
        activeRef.current = false;
        if (data.status === 'ENDED') toast.success('Call completed');
        else if (data.status === 'DEVICE_OFFLINE') toast.error('Your GNONS phone appears offline — command pending.');
        else if (data.status === 'PERMISSION_DENIED') toast.error('Call permission denied on your phone.');
        else if (data.status === 'EXPIRED') toast.error('Call command expired — phone did not respond.');
        else if (data.status === 'CANCELLED') toast('Call cancelled on phone.');
        else if (data.status === 'FAILED') toast.error('Call failed on phone.');
        setTimeout(() => setStatus(null), 4000);
        return;
      }
      setTimeout(() => poll(id, tries + 1), 3000);
    } catch (e) { setDialing(false); activeRef.current = false; }
  }, []);

  const send = useCallback(async (body, { delegated = false } = {}) => {
    activeRef.current = true;
    setDialing(true);
    setStatus('REQUESTED');
    try {
      const { data } = await callingAPI.clickToCall(body);
      setStatus(data.status);
      toast.success(data.duplicate
        ? 'Call already sent to the GNONS phone'
        : delegated
          ? `Call sent to ${data.caller_name} · ${data.masked_device}`
          : 'Call sent to your GNONS phone');
      poll(data.command_id, 0);
      return true;
    } catch (e) {
      setDialing(false);
      setStatus(null);
      activeRef.current = false;
      const s = e.response?.status;
      const raw = e.response?.data?.detail;
      const d = typeof raw === 'string' ? raw : (raw?.message || raw?.code || '');
      const code = typeof raw === 'object' ? raw?.code : (typeof d === 'string' ? d.split(':')[0] : '');
      if (code === 'CALLER_SELECTION_REQUIRED' || code === 'DEVICE_SELECTION_REQUIRED') {
        // No silent fallback: the caller employee / device must be picked explicitly.
        setSelection({ ...body, reason: code, devices: raw?.devices || [], leadOwnerId: raw?.lead_owner_id || null });
        return false;
      }
      if (/DEVICE_OWNERSHIP_MISMATCH/.test(d)) toast.error('That device does not belong to the selected caller.');
      else if (/DEVICE_NOT_ACTIVE/.test(d)) toast.error('The selected device is not an active enrollment.');
      else if (/DEVICE_NOT_REGISTERED/.test(d)) toast.error('No GNONS Calling device registered for that employee.');
      else if (/DELEGATION_NOT_AUTHORIZED/.test(d)) toast.error('You are not allowed to call from another employee’s device.');
      else if (s === 403) toast.error('Calling access is disabled by Admin.');
      else if (s === 409) toast.error(d || 'Call could not be routed.');
      else toast.error('Could not send the call to the GNONS phone.');
      return false;
    }
  }, [poll]);

  const startCall = useCallback(async (phone, opts = {}) => {
    const { leadId, leadName } = opts;
    if (!phone) { toast.error('No phone number available to call.'); return; }
    if (user?.calling_controls?.click_to_call_access === false) {
      toast.error('Calling access is disabled by Admin.');
      return;
    }
    if (activeRef.current) { toast('Call already being sent to your phone…'); return; }
    await send({ phone_number: phone, lead_id: leadId, lead_name: leadName });
  }, [user, send]);

  // Explicit delegated dial: a chosen caller employee + their own active device.
  const sendDelegated = useCallback(async (callerEmployeeId, targetDeviceId) => {
    if (!selection) return;
    const ok = await send({ ...selection, reason: undefined, devices: undefined, leadOwnerId: undefined,
      caller_employee_id: callerEmployeeId, target_device_id: targetDeviceId }, { delegated: true });
    if (ok) setSelection(null);
  }, [selection, send]);

  const previewDelegated = useCallback(async (callerEmployeeId, targetDeviceId) => {
    if (!selection) return null;
    try {
      const { data } = await callingAPI.clickToCall({
        phone_number: selection.phone_number, lead_id: selection.lead_id, lead_name: selection.lead_name,
        caller_employee_id: callerEmployeeId, target_device_id: targetDeviceId, dry_run: true });
      return data;
    } catch (e) {
      const raw = e.response?.data?.detail;
      return { error: typeof raw === 'string' ? raw : (raw?.message || 'Selection not allowed') };
    }
  }, [selection]);

  return { startCall, dialing, status, selection, setSelection, sendDelegated, previewDelegated };
}

export default useClickToCall;
